import { motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

/**
 * Subtle, brand-appropriate page transition.
 *
 * Design goals (Wimbledon / Hurlingham tone):
 *   - Quiet fade only. No slide, no scale, no bounce.
 *   - Fast (180ms in, 120ms hint) — the user must never feel the site is
 *     lagging. Anything > ~250ms starts to feel slow on repeat navigations.
 *   - Respects `prefers-reduced-motion`: users who opt out get zero motion.
 *
 * We key the wrapper on `pathname` so React remounts + re-fades on every
 * client-side route change.
 *
 * Note: we deliberately do NOT wrap in AnimatePresence with mode="wait" —
 * that adds a mandatory ~200ms "wait for exit" pause on every click, which
 * feels sluggish on mobile. Fade-in only is snappier and equally clear.
 */
const FADE_TRANSITION = { duration: 0.18, ease: 'easeOut' };

const PageTransition = ({ children }) => {
  const reduce = useReducedMotion();
  const { pathname } = useLocation();

  if (reduce) {
    // Fully static fallback for users with prefers-reduced-motion.
    return <>{children}</>;
  }

  return (
    <motion.div
      key={pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={FADE_TRANSITION}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;
